import * as _ from 'lodash-es';
import { useState, useCallback } from 'react';

import widthIcon from '@/assets/icons/width_icon.svg';
import { kebabCase, moveElementToBack } from '@/utils';

import { InputRendererProps } from '../inputs';
import { findFieldsByType } from '../store';
import {
  GO,
  TKEvent,
  CATALOGUE_CATEGORY as CAT,
  CATALOGUE_TYPE,
  LENGTH_TYPE as LT,
  NIDSymbolEntity,
  FIELD_Z0,
  OMIT_FROM_LENGTH,
} from '../types';
import {
  getDimensionBy,
  setDimensionBy,
  getSystemBy,
} from '../types/dimension-setting';

export const usePipeLength = (lazyCloseHandler: (type?: string) => void) => {
  // use cached system
  const lastSystem = getSystemBy(LT.P) || '';
  const [currentSystem, setCurrentSystem] = useState(lastSystem); // system belonged

  // check dynamic fields for PIPE_LENGTH:
  // @2025/04/25
  const plFields = findFieldsByType(LT.P);

  // == check last used dimension to determine current values:
  // == this will only take effect in modal openning!
  const lastDimension = getDimensionBy(LT.P);
  const lastFieldPairs = _.pick(lastDimension, plFields) as GO;
  const [fieldProps, setFieldProps] = useState(lastFieldPairs);

  /**
   * figure out the attributes displayed on the right side panel
   * @returns `{}`
   */
  const createFeatureEntity = (): NIDSymbolEntity => {
    const detail: NIDSymbolEntity = {
      category: CAT.D,
      itemType: CATALOGUE_TYPE.L,
      system: currentSystem,
      entityType: LT.P,
      icon: widthIcon,
      toolType: LT.P,
      ...fieldProps,
    };

    return detail;
  };

  const applyNewElementHandler = () => {
    const detail = createFeatureEntity();
    // lazy notification to map to add new element!
    setTimeout(() => {
      const event = new CustomEvent(TKEvent.NEWLINE, { detail });
      document.dispatchEvent(event);
    }, 200);

    // reset global filter type! - 2025/03/25
    lazyCloseHandler(LT.P);

    // cache input fields for current duct type
    setDimensionBy(LT.P, {
      ...fieldProps,
      system: currentSystem,
    });
  };

  // lazy close, and dispatch cancel tool event...
  const cancelNewElementHandler = () => {
    lazyCloseHandler();
    const evt = new Event(TKEvent.CANCEL_DRAWING_TOOL);
    document.dispatchEvent(evt);
  };

  /**
   * TODO: anyway to separate this function out of this hook? - 2023/07/13
   *
   * figure out the properties needed for each field input component
   *
   * @param fieldName each field name
   * @returns normal object
   */
  const inputFieldRenderer = (fieldName: string): InputRendererProps => {
    const kebabField = kebabCase(fieldName);
    const fieldValueChangeHandler = (key: string, value: string) => {
      // console.log({ key });
      const changedProps = { ...fieldProps, [key]: value };
      setFieldProps(changedProps);
    };
    const inputProps: InputRendererProps = {
      ...fieldProps,
      fieldName, // `DefaultInput` need this
      fieldValue: fieldProps[kebabField], // `DefaultInput` need this
      fieldHandler: fieldValueChangeHandler,
      blurHandler: () => null, // FIXME: placeholder for now - 2023/11/08
    };

    return inputProps;
  };

  /**
   * infer fields to show by `shape`
   */
  const ductFieldsFilterBy = useCallback(() => {
    const reOrderVerticalLength = (fields: string[]) => {
      const zIndex = fields.indexOf(FIELD_Z0);
      let tempFields = fields;
      if (zIndex > -1) {
        // @2025/04/03
        tempFields = moveElementToBack(fields, zIndex) as string[];
      }
      return tempFields;
    };

    let filteredFields = plFields;
    // then, remove "length_1", "length_unit_str" - @2025/04/22
    filteredFields = _.difference(filteredFields, OMIT_FROM_LENGTH);

    // re-ordered fields
    return reOrderVerticalLength(filteredFields);
  }, [plFields]);

  return {
    currentSystem,
    setCurrentSystem,
    fieldProps,
    inputFieldRenderer,
    pipeLengthFields: ductFieldsFilterBy(),
    cancelNewElementHandler,
    applyNewElementHandler,
  };
};
